import {useMemo, useState} from 'react';

import ApiCaller from '../../utils/ApiCaller';

export interface useApiCallProps {
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  payload?: any;
  cachingMethod?: 'session' | 'local';
  skip?: boolean;
}
export function useApiCall<T>({
  url,
  method,
  payload,
  cachingMethod,
  skip,
}: useApiCallProps): [T | undefined, boolean] {
  const [data, setData] = useState<T | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);

  useMemo(() => {
    async function fetchData() {
      try {
        if (loading === true || skip === true) {
          return;
        }

        setLoading(true);

        switch (cachingMethod) {
          case 'session': {
            // Check if data exists in session storage
            const dataInSessionStorage = sessionStorage.getItem(url);
            if (dataInSessionStorage) {
              setData(JSON.parse(dataInSessionStorage));
              setLoading(false);
              return;
            }
            break;
          }

          case 'local': {
            // Check if data exists in local storage
            const dataInLocalStorage = localStorage.getItem(url);
            if (dataInLocalStorage) {
              setData(JSON.parse(dataInLocalStorage));
              setLoading(false);
              return;
            }
            break;
          }

          default:
            // No caching. Continue to API call.
            break;
        }

        // Call API and store result in local storage
        const response = await ApiCaller({
          method: method,
          endpointURL: url,
          data: payload,
        });
        const data = (await response.data) as T;
        setData(data);
        setLoading(false);

        switch (cachingMethod) {
          case 'session':
            sessionStorage.setItem(url, JSON.stringify(data));
            break;

          case 'local':
            localStorage.setItem(url, JSON.stringify(data));
            break;

          default:
            break;
        }
      } catch (error) {
        setLoading(false);
        console.error(error);
      }
    }

    void fetchData();
  }, [url, payload, method, skip]);

  return [data, loading];
}
